import { CreoMatParameterItem, WizardPayload, CacheEntry } from "../types/PluginTypes";

declare global {
  interface Window {
    CreoJS?: {
      $ADD_ON_LOAD: (callback: () => void) => void;
      $INITIALIZE: () => void;
      getActiveModelName: () => Promise<string>;
      getCurrentMaterialName: () => Promise<string>;
      getMatParameterFromModel: () => Promise<CreoMatParameterItem[]>;
      processReceivedMaterial: (payload: WizardPayload) => Promise<boolean>;
      removeAssignedMaterial: (payload: { partNumber?: string; bmId?: string; bmName?: string }) => Promise<boolean>;
      clearAllPluginParameters: (payload: { partNumber?: string }) => Promise<boolean>;
      setAllModelParameters: (payload: WizardPayload) => Promise<void>;
      setSurfaceTreatmentParameters: (payload: WizardPayload) => Promise<void>;
      setModelParameters: (payload: WizardPayload) => Promise<void>;
      writeRecentMaterialToCache: (payload: WizardPayload) => Promise<CacheEntry[]>;
      getCurrentMatListFromCache: () => Promise<CacheEntry[]>;
      getCurrentMaterialDensity: () => Promise<string>;
      getMaterialDataFromCacheByKey: (rowKey: string) => Promise<WizardPayload>;
      deleteCacheFile: () => Promise<void>;
      deleteCacheEntryByKey: (rowKey: string) => Promise<CacheEntry[]>;
    };
  }
}

export function waitForCreoReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!window.CreoJS) {
      reject(new Error("CreoJS is not loaded."));
      return;
    }

    window.CreoJS.$ADD_ON_LOAD(() => resolve());
  });
}

export async function getActiveModelName(): Promise<string> {
  await waitForCreoReady();
  return window.CreoJS!.getActiveModelName();
}

export async function getCurrentMaterialName(): Promise<string> {
  await waitForCreoReady();
  return window.CreoJS!.getCurrentMaterialName();
}

export async function getMatParameterFromModel(): Promise<CreoMatParameterItem[]> {
  await waitForCreoReady();
  return window.CreoJS!.getMatParameterFromModel();
}

export async function processReceivedMaterial(payload: WizardPayload): Promise<boolean> {
  await waitForCreoReady();
  return window.CreoJS!.processReceivedMaterial(payload);
}

export async function removeAssignedMaterial(payload: { partNumber?: string; bmId?: string; bmName?: string }): Promise<boolean> {
  await waitForCreoReady();
  return window.CreoJS!.removeAssignedMaterial(payload);
}

export async function clearAllPluginParameters(payload: { partNumber?: string }): Promise<boolean> {
  await waitForCreoReady();
  return window.CreoJS!.clearAllPluginParameters(payload);
}

export async function setAllModelParameters(payload: WizardPayload): Promise<void> {
  await waitForCreoReady();
  return window.CreoJS!.setAllModelParameters(payload);
}

export async function setSurfaceTreatmentParameters(payload: WizardPayload): Promise<void> {
  await waitForCreoReady();
  return window.CreoJS!.setSurfaceTreatmentParameters(payload);
}

export async function setModelParameters(payload: WizardPayload): Promise<void> {
  await waitForCreoReady();
  return window.CreoJS!.setModelParameters(payload);
}

export async function writeRecentMaterialToCache(payload: WizardPayload): Promise<CacheEntry[]> {
  await waitForCreoReady();
  return window.CreoJS!.writeRecentMaterialToCache(payload);
}

export async function getCurrentMatListFromCache(): Promise<CacheEntry[]> {
  await waitForCreoReady();
  return window.CreoJS!.getCurrentMatListFromCache();
}

export async function getCurrentMaterialDensity(): Promise<string> {
  await waitForCreoReady();
  return window.CreoJS!.getCurrentMaterialDensity();
}

export async function getMaterialDataFromCacheByKey(rowKey: string): Promise<WizardPayload> {
  await waitForCreoReady();
  return window.CreoJS!.getMaterialDataFromCacheByKey(rowKey);
}

export async function deleteCacheFile(): Promise<void> {
  await waitForCreoReady();
  return window.CreoJS!.deleteCacheFile();
}

export async function deleteCacheEntryByKey(rowKey: string): Promise<CacheEntry[]> {
  await waitForCreoReady();
  return window.CreoJS!.deleteCacheEntryByKey(rowKey);
}